import React from 'react';

import { Flex, Modal, Space } from 'antd-v5';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Text } from 'scouting-ui/lib';

import type { YouthMBCRemovalItem } from '@appTypes/esb';
import type { ESBBaseError } from '@appTypes/esb/errors';
import { useMBDict } from '@dict/duck/hooks';
import { useRemoveMBCYouthMBMutation } from '@modules/meritBadgeCounselors/api';
import tagFactory from '@modules/rtk/utils/tagFactory';
import { intl } from '@shared/localization';
import { getPreferredName } from '@shared/utils/preferredName';
import { toastService } from '@toasts';

import { ViewByTabs } from '../duck/constants';
import {
  dashboardViewBySel,
  dashboardYouthsByMIDSel,
  mbcProfileGuidSel,
  mbcProfileMIDSel,
  modalSel,
  selectedYouthByMBSel,
  selectedYouthSel,
} from '../duck/selectors';
import { mbcDashboardActions } from '../duck/slice';
import { YouthByMBRowKey } from '../duck/utils';
import MBImage from './MBImage';

export default function RemoveMBModal() {
  const { meritBadgesById } = useMBDict();
  const viewBy = useSelector(dashboardViewBySel);
  const selectedYouthByMB = useSelector(selectedYouthByMBSel);
  const selectedYouth = useSelector(selectedYouthSel);
  const youthsByMID = useSelector(dashboardYouthsByMIDSel);
  const selection =
    viewBy === ViewByTabs.Youth
      ? selectedYouth.flatMap(memberId =>
          (youthsByMID[memberId]?.meritBadgesCounseling ?? []).map(mb =>
            YouthByMBRowKey.getKey({ memberId, meritBadgeId: mb.meritBadgeId }),
          ),
        )
      : selectedYouthByMB;
  const open = useSelector(modalSel('removeYouth'));
  const mbcMemberId = useSelector(mbcProfileMIDSel);
  const mbcPersonGuid = useSelector(mbcProfileGuidSel);

  const [removeCounselor, { isLoading: isRemoving }] =
    useRemoveMBCYouthMBMutation();

  const dispatch = useDispatch();

  return (
    <Modal
      title={`${intl.formatMessage({ id: 'shared.confirm' })}`}
      cancelButtonProps={{ type: 'default', disabled: isRemoving }}
      okButtonProps={{ danger: true }}
      okText={<FormattedMessage id="shared.remove" />}
      open={open}
      onOk={async () => {
        try {
          if (!mbcMemberId || !mbcPersonGuid) return;

          const payloadYouths = _.chain(selection)
            .map(key => {
              const { memberId, meritBadgeId } = YouthByMBRowKey.parseKey(key);
              return {
                memberId,
                meritBadgeId,
              };
            })
            .groupBy(item => item.memberId)
            .map(
              (values, memberId) =>
                ({
                  youthMemberId: +memberId,
                  meritBadges: values.map(item => item.meritBadgeId),
                } as YouthMBCRemovalItem),
            )
            .value();

          await removeCounselor({
            body: {
              counselorMemberId: mbcMemberId,
              youths: payloadYouths,
            },
            extraTags: [tagFactory.MBCDashboard.userDashboard(mbcPersonGuid)],
          }).unwrap();
          toastService.success(
            intl.formatMessage({
              id: 'mbcDashboard.removeYouthMBModal.success',
            }),
          );
          dispatch(
            mbcDashboardActions.setModal({ modal: 'removeYouth', open: false }),
          );
          dispatch(
            mbcDashboardActions.resetItem([
              'youthSearch',
              'selectedYouthByMB',
              'selectedYouth',
            ]),
          );
        } catch (err) {
          const error = err.data as ESBBaseError;
          toastService.error(error?.errorDesc);
        }
      }}
      onCancel={() =>
        dispatch(
          mbcDashboardActions.setModal({ modal: 'removeYouth', open: false }),
        )
      }
      onClose={() =>
        dispatch(
          mbcDashboardActions.setModal({ modal: 'removeYouth', open: false }),
        )
      }
    >
      <Flex vertical gap="small">
        <Text paragraph>
          <FormattedMessage id="mbcDashboard.removeYouthMBModal.description" />
        </Text>

        {_.chain(selection)
          .map(key => ({ ...YouthByMBRowKey.parseKey(key), key }))
          .groupBy('meritBadgeId')
          .map((values, key) => {
            const { meritBadgeId } = values[0];
            const mb = meritBadgesById.get(meritBadgeId);
            const mbImage = mb?.imageUrl100;

            const youthNames = values
              .map(mbItem => {
                const youth = youthsByMID[mbItem.memberId]!;

                return getPreferredName(youth);
              })
              .join(', ');

            return (
              <Space direction="vertical" key={key}>
                <Space align="center" key={key}>
                  <MBImage src={mbImage} width={32} height={32} />
                  <Text>{mb?.name}</Text>
                </Space>
                <Text>{youthNames}</Text>
              </Space>
            );
          })
          .value()}
      </Flex>
    </Modal>
  );
}
